import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { API_BASE_URL } from '../config';
import { getTelegramInitData } from '../utils/platform';
import { attachAuthInterceptors } from './attachAuthInterceptors';
import {
  AUTH_EVENT_LOGOUT,
  AUTH_EVENT_REFRESHED,
  broadcastAuth,
  clearBrowserSession,
  getAccessToken,
  getTokenRefreshedElsewhere,
  storeAccessToken,
} from './sessionStore';

console.log('🌐 AuthService baseURL:', API_BASE_URL);

// === Создаём экземпляр Axios ===
const api = axios.create({
  baseURL: API_BASE_URL,
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let refreshPromise = null;

const browserAuthConfig = {
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
};

const REFRESH_LOCK = 'pr-auth-refresh';

// Refresh-токен одноразовый (ротация), а cookie общий для всех вкладок и установленной PWA.
// Поэтому refresh делает только одна вкладка за раз (Web Locks), остальные берут её результат.
async function refreshWithCoordination() {
  const tokenBefore = getAccessToken();

  const doRefresh = async () => {
    // Пока ждали блокировку, другая вкладка могла уже обновить токен
    const refreshedElsewhere = getTokenRefreshedElsewhere(tokenBefore);
    if (refreshedElsewhere) return refreshedElsewhere;

    try {
      const response = await refreshClient.post('/auth/refresh', null, browserAuthConfig);
      const accessToken = response.data?.access_token;
      if (!accessToken) {
        throw new Error('Не удалось обновить сессию');
      }
      storeAccessToken(accessToken);
      broadcastAuth(AUTH_EVENT_REFRESHED);
      return accessToken;
    } catch (error) {
      // 401 «Сессия уже обновлена» и т.п.: возможно, свежий токен уже лежит в хранилище
      const fallback = getTokenRefreshedElsewhere(tokenBefore);
      if (fallback) return fallback;
      throw error;
    }
  };

  if (navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK, doRefresh);
  }
  return doRefresh();
}

export async function refreshSession() {
  // Внутри вкладки — один refresh на всех (работает и без Web Locks)
  if (!refreshPromise) {
    refreshPromise = refreshWithCoordination().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * === Авторизация через Telegram ===
 * Отправляет данные Telegram WebApp API целиком на сервер и сохраняет токен доступа.
 *
 * @param {string} tgWebAppData - строка данных Telegram WebApp (из window.Telegram.WebApp.initData)
 * @returns {Promise<string>} Возвращает access_token
 */
export const handleTelegramAuth = async (tgWebAppData) => {
  try {
    console.log('🔹 Авторизация через Telegram...');
    try {
      window.dispatchEvent(new Event('auth-token-updated'));
    } catch {}

    // ✅ Отправляем данные именно как объект (а не просто строку)
    const response = await api.post('/auth/telegram', { tgWebAppData });

    if (response.status === 200 && response.data?.access_token) {
      const { access_token } = response.data;

      // ✅ Сохраняем токен под ключом "token" (единое имя для всего приложения)
      localStorage.setItem('token', access_token);
      console.log('✅ Авторизация успешна, токен сохранён.');

      return access_token;
    } else {
      console.error('⚠️ Ошибка авторизации:', response.data);
      throw new Error('Ошибка авторизации на сервере.');
    }
  } catch (error) {
    console.error('❌ Ошибка выполнения запроса:', error);
    throw error;
  }
};

let telegramAuthPromise = null;

export function reauthenticateTelegram() {
  const initData = getTelegramInitData();
  if (!initData) {
    return Promise.reject(new Error('Нет данных Telegram для входа'));
  }
  if (!telegramAuthPromise) {
    telegramAuthPromise = handleTelegramAuth(initData).finally(() => {
      telegramAuthPromise = null;
    });
  }
  return telegramAuthPromise;
}

attachAuthInterceptors(api, { refreshSession, reauthenticateTelegram });

export const loginWithEmail = async (email, password) => {
  const response = await api.post('/auth/login', { email, password }, browserAuthConfig);
  const accessToken = response.data?.access_token;
  if (!accessToken) {
    throw new Error('Не удалось войти');
  }
  storeAccessToken(accessToken);
  return response.data;
};

export const registerWithEmail = async (email, password) => {
  const response = await api.post('/auth/register', { email, password }, browserAuthConfig);
  const accessToken = response.data?.access_token;
  if (!accessToken) {
    throw new Error('Не удалось зарегистрироваться');
  }
  storeAccessToken(accessToken);
  return response.data;
};

// Возвращает текст ответа (одинаковый для любого email)
export const requestPasswordReset = async (email) => {
  const response = await api.post('/auth/password/forgot', { email });
  return response.data?.detail || '';
};

export const resetPassword = async (token, password) => {
  await api.post('/auth/password/reset', { token, password });
};

export const verifyEmail = async (token) => {
  await api.post('/auth/verify-email', { token });
};

export const sendVerificationEmail = async () => {
  await api.post('/auth/verify-email/send');
};

export const logout = async () => {
  try {
    await api.post('/auth/logout', null, browserAuthConfig);
  } catch (error) {
    console.warn('Не удалось отозвать сессию на сервере:', error?.message || error);
  }
  clearBrowserSession();
  localStorage.removeItem('activeServer');
  localStorage.removeItem('selectedGroup');
  broadcastAuth(AUTH_EVENT_LOGOUT);
  window.location.replace('/login');
};

/**
 * === Проверка токена ===
 * Проверяет, действителен ли токен через /auth/me
 */
export const verifyToken = async (accessToken) => {
  try {
    const response = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.status === 200 ? response.data : null;
  } catch {
    return null;
  }
};

/**
 * === Проверка срока действия JWT ===
 * @param {string} token - JWT токен для проверки
 * @returns {boolean} true — если токен истёк, иначе false
 */
export const isTokenExpired = (token) => {
  try {
    const decoded = jwt_decode(token);
    const now = Date.now() / 1000;
    return decoded.exp < now;
  } catch (error) {
    console.error('Ошибка при проверке срока действия токена:', error);
    return true;
  }
};

export default api;
